import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'microsoft-entra',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'useCredential',
      label: 'Use OAuth2 Credential',
      type: 'boolean',
      default: true,
      description: 'Use stored OAuth2 credential for user-level access to Entra ID'
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'microsoft_entra_oauth',
      },
      displayOptions: {
        show: {
          useCredential: [true]
        }
      },
      description: 'Select Microsoft Entra ID OAuth2 credential'
    },

    // === Resource Selection ===
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'user',
      options: [
        { value: 'user', name: 'User', description: 'Work with directory users' },
        { value: 'group', name: 'Group', description: 'Work with directory groups' }
      ]
    },

    // === USER Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'get',
      displayOptions: {
        show: {
          resource: ['user']
        }
      },
      options: [
        { value: 'get', name: 'Get', description: 'Get a user by ID or UPN' },
        { value: 'getAll', name: 'Get Many', description: 'List and filter users' }
      ]
    },

    // === GROUP Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'get',
      displayOptions: {
        show: {
          resource: ['group']
        }
      },
      options: [
        { value: 'get', name: 'Get', description: 'Get a group by ID' },
        { value: 'getAll', name: 'Get Many', description: 'List and filter groups' }
      ]
    },

    // ===============================
    // USER PROPERTIES
    // ===============================

    // Get User - User selection with expression toggle
    {
      key: 'userId',
      label: 'User',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['user'],
          operation: ['get']
        }
      },
      description: 'Select the user or enter a User ID / UPN',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getUsers',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Get Many Users - Filter
    {
      key: 'filterExpression',
      label: 'OData Filter',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['user'],
          operation: ['getAll']
        }
      },
      description: 'OData filter expression. Example: startsWith(displayName, \'John\') or department eq \'Engineering\''
    },

    // ===============================
    // GROUP PROPERTIES
    // ===============================

    // Get Group - Group selection with expression toggle
    {
      key: 'groupId',
      label: 'Group',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['group'],
          operation: ['get']
        }
      },
      description: 'Select the group or enter a Group ID',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getGroups',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Include Members for Get Group
    {
      key: 'includeMembers',
      label: 'Include Members',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['group'],
          operation: ['get']
        }
      },
      description: 'Include group members in the response'
    },

    // Get Many Groups - Filter
    {
      key: 'filterExpression',
      label: 'OData Filter',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['group'],
          operation: ['getAll']
        }
      },
      description: 'OData filter expression. Example: startsWith(displayName, \'Engineering\') or mailEnabled eq true'
    },

    // ===============================
    // COMMON PROPERTIES
    // ===============================

    // Return All
    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          operation: ['getAll']
        }
      },
      description: 'Return all results or limit the number'
    },

    // Limit
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      displayOptions: {
        show: {
          operation: ['getAll'],
          returnAll: [false]
        }
      },
      description: 'Maximum number of results to return'
    },

    // Simplify Output
    {
      key: 'simplifyOutput',
      label: 'Simplify Output',
      type: 'boolean',
      default: true,
      description: 'Return a simplified version of the response with key fields only'
    }
  ]
};

export default schema;
